import * as _ from 'lodash'
import { DateUtils } from "../PA.Core/Basic/Dates";
import {IBusinessObject, IBusinessObjectCreateLoadOptions} from '../PA.Core/PA.Core.IF.BO'
import {IMessageProvider, IProgressProvider, MessageType} from '../PA.Core/PA.Core.IF.Basic'
import {PAdbContext} from '../PA.Core/PAdbContext'
import {IdentityProvider} from '../PA.Core/Basic/Identity'
import {MessageProvider} from '../PA.Core/Basic/Messages'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {
    WB_BaselineRow,
    WB_CompanyRow,
    WB_JobProcessRow,
    WB_MasterDataSetRow,
    WB_PartitionRRow,
    WB_SalesAreaRow,
    WB_SalesOrgRow,
} from "../PA.Core/PAdbSchemaRows";
import {IwbBaseline} from "../PA.Core/PAdbSchemaRowsIF"
import {
    esWB_Baseline,
    esWB_Baseline_Metrics,
    esWB_Company,
    esWB_Job_Process,
    esWB_MasterData_Set,
    esWB_Partition,
    esWB_PartitionR,
    esWB_SalesArea,
    esWB_SalesOrg,
} from "../PA.Core/PAdbSchema";
import {ESDataStore} from "../PA.Core/lib/ESDataStore"
import {ESRecord} from "../PA.Core/lib/ESRecord"
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver"
import {ASync} from "../PA.Core/Basic/ASync"
import {BaselineBOValidations} from "./BaselineBO.Validations"
import {PAEnvironment} from "../PA.Core/PAEnvironment"
import {BehaviorSubject} from "rxjs";
import {PartitionBO} from "./PartitionBO";
import {BaselineBOAudit} from "./BaselineBO.Audit";
import {AuditService} from "../PA.Core/Security/AuditService";
import { IJobParam } from "../PA.Job.Core/PA.Job.IF.JobParam";
import { JobParamManager } from "../PA.Job.Core/JobParamManager";
import {MDUsageService} from "../PA.MD.Services/MDUsageService";

export interface IBaselineLoadOptions {
    dbContext?: PAdbContext
}

const BaselineLoadOptions_default: IBaselineLoadOptions = {}

export class BaselineBO implements IBusinessObject {

    //<editor-fold desc = "Properties and Constructor">

    Identity: IdentityProvider

    CreateOptions: any

    LoadOptions: any
    _bIsLoaded: boolean
    _OnLoadComplete: Promise<IBusinessObject>
    LoadProgressProvider: IProgressProvider

    ValidateOptions: any
    _bIsValidated: boolean
    ValidateMessageProvider: IMessageProvider
    ValidateProgressProvider: IProgressProvider

    SaveOptions: any
    _bIsSaved: boolean
    SaveProgressProvider: IProgressProvider

    DeleteOptions: any
    _bIsDeleted: boolean
    DeleteMessageProvider: IMessageProvider
    DeleteProgressProvider: IProgressProvider

    dbContext: PAdbContext
    drWB_Baseline: WB_BaselineRow

    private _BaselinePropertyChanged: BehaviorSubject<any>
    partitionBO: PartitionBO


    constructor(options?: IBusinessObjectCreateLoadOptions) {

        this.CreateOptions = options || {}

        if ('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext

        this.Identity = new IdentityProvider({id: 0, name: 'New Baseline'})

        this.LoadOptions = {}
        this._bIsLoaded = false
        this._OnLoadComplete = null
        this.LoadProgressProvider = new ProgressProvider('Loading', this)

        this.ValidateOptions = {}
        this._bIsValidated = false
        this.ValidateMessageProvider = new MessageProvider()
        this.ValidateProgressProvider = new ProgressProvider("Validating", this)

        this.SaveOptions = {}
        this._bIsSaved = false
        this.SaveProgressProvider = new ProgressProvider("Saving", this)

        this.DeleteOptions = {}
        this._bIsDeleted = false
        this.DeleteMessageProvider = new MessageProvider()
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this)

        this.drWB_Baseline = null

        this._BaselinePropertyChanged = new BehaviorSubject<any>(undefined)
        this.partitionBO = undefined
    }

    //</editor-fold>

    get WB_Baseline(): WB_BaselineRow {
        return this.drWB_Baseline
    }

    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: IBusinessObjectCreateLoadOptions): Promise<IBusinessObject> {
        const self = this
        this.LoadProgressProvider.start("Creating Baseline")

        let internalOnComplete = this._OnLoadComplete = (async () => {
            try {

                self.LoadOptions = options || {}
                if ('dbContext' in self.LoadOptions)
                    self.dbContext = self.LoadOptions.dbContext
                else
                    self.dbContext = new PAdbContext()

                if (_.isEmpty(self.dbContext)) {
                    let sError = "Unable to determine dbContext for new Baseline."
                    self._OnLoadComplete = null
                    self.LoadProgressProvider.exception(sError)
                    return self
                }

                // Create record using dbContext
                let newBaselineRow: any = <any>await self.dbContext.createRecordWithKey(esWB_Baseline)

                newBaselineRow.setDefaults()
                self.drWB_Baseline = newBaselineRow
                self.dbContext.add(esWB_Baseline, newBaselineRow)
                self.Identity.setAll(self.WB_Baseline.cif.WB_BASELINE_ID, self.WB_Baseline.cif.BASELINE_ID, self.WB_Baseline.cif.BASELINE_NAME)
                self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")")
                await self.applyCreateOptions()
                self.Identity.setAll(self.WB_Baseline.cif.WB_BASELINE_ID, self.WB_Baseline.cif.BASELINE_ID, self.WB_Baseline.cif.BASELINE_NAME)
                self.Identity.listenToRecordUpdates(self.WB_Baseline, 'WB_BASELINE_ID', 'BASELINE_ID', 'BASELINE_NAME')

                // create job param record
                await self.IJobParam_OnLoadComplete()

                //creating partition record
                let apartitionBO: PartitionBO = new PartitionBO({dbContext: self.dbContext})
                let aPartitionBO:any = <any>await apartitionBO.LoadNew()


                aPartitionBO.WB_Partition.cif.CUSTOMER_IND = '1'
                aPartitionBO.WB_Partition.cif.PRODUCT_IND = '1'

                self.WB_Baseline.WB_PartitionRow = aPartitionBO.WB_Partition
                self.WB_Baseline.cif.WB_PARTITION_ID = aPartitionBO.WB_Partition.cif.WB_PARTITION_ID

                self.partitionBO = aPartitionBO

                self.WB_Baseline.setupOnChangeEvents(["WB_CUSTH_ID", "WB_PRDH_ID", "WB_BUSINESSUNIT_ID"], self.Baseline_onRecordChange.bind(self), self)

                self.LoadProgressProvider.finish()
                self._bIsLoaded = true
                self._OnLoadComplete = null
                return self

            }catch(err) {
                    self.LoadProgressProvider.exception(err)
                    return self
                }

        })();

        return this._OnLoadComplete
    }

    async applyCreateOptions() {
        const self = this
        let sID = self.CreateOptions?.sId || self.LoadOptions?.sId
        let sName = self.CreateOptions?.sName || self.LoadOptions?.sName

        if (!_.isEmpty(sID))
            self.WB_Baseline.cif.BASELINE_ID = sID

        if (!_.isEmpty(sName))
            self.WB_Baseline.cif.BASELINE_NAME = sName

        // Set Master Data Set
        let wbMasterDataSet = self.CreateOptions?.wbMasterDataSetId || self.LoadOptions?.wbMasterDataSetId
        if (_.isNumber(wbMasterDataSet)) {
            let mds: WB_MasterDataSetRow = <any>await self.dbContext.find(esWB_MasterData_Set, wbMasterDataSet)
            if (mds) {
                self.WB_Baseline.WB_MasterDataSetRow = mds
                self.WB_Baseline.cif.WB_MDSET_ID = wbMasterDataSet
            }
        }

        // Set Sales Org
        let wbSalesOrg = self.CreateOptions?.wbSalesOrgId || self.LoadOptions?.wbSalesOrgId
        if (_.isNumber(wbSalesOrg)) {
            let salesOrg: WB_SalesOrgRow = <any>await self.dbContext.find(esWB_SalesOrg, wbSalesOrg)
            if (salesOrg) {
                self.WB_Baseline.WB_SalesOrgRow = salesOrg
                self.WB_Baseline.cif.WB_SALESORG_ID = wbSalesOrg
                // set Company Row
                salesOrg.WB_CompanyRow = <WB_CompanyRow>await self.dbContext.find(esWB_Company, salesOrg.cif.WB_COMPANY_ID)
            }
        }

        // Set Sales Area
        let wbSalesArea = self.CreateOptions?.wbSalesAreaId || self.LoadOptions?.wbSalesAreaId
        if (_.isNumber(wbSalesArea)) {
            let salesArea: WB_SalesAreaRow = <any>await self.dbContext.find(esWB_SalesArea, wbSalesArea)
            if (salesArea) {
                self.WB_Baseline.WB_SalesAreaRow = salesArea
                self.WB_Baseline.cif.WB_SALESAREA_ID = wbSalesArea
            }
        }

    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {

        const self = this
        if (!this.bIsLoaded)
            this.Identity.setAll( anId, anId,'Loading Baseline (' + anId + ')')

        let recordResolver: ESRecordResolver
        let recordResolverLoadPromise: Promise<void>

        self.LoadProgressProvider.start("Loading (" + anId + ")")

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {

            if (self.bIsLoaded) {
                const sError = "Baseline already loaded."
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.exception(sError)
                error(sError)
                return
            }

            self.LoadOptions = options || {}

            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext
            else
                self.dbContext = new PAdbContext()

            if (_.isEmpty(self.dbContext)) {
                const sError = "Unable to determine dbContext for Baseline."
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.exception(sError)
                error(sError)
                return
            }

            if ('RecordResolver' in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver
                recordResolverLoadPromise = ASync.resolve()
            } else {
                recordResolver = BaselineBO.createRecordResolver({dbContext: self.dbContext})
                recordResolverLoadPromise = BaselineBO.setupRecordResolver(recordResolver, [anId])
            }

            recordResolverLoadPromise
                .then(function () {
                    let aBaselineRow: any = recordResolver.get(esWB_Baseline, anId)

                    if (_.isEmpty(aBaselineRow)) {
                        error("Baseline not Found")
                        return
                    }

                    self.drWB_Baseline = aBaselineRow
                    self.Identity.setAll(self.WB_Baseline.cif.WB_BASELINE_ID, self.WB_Baseline.cif.BASELINE_ID, self.WB_Baseline.cif.BASELINE_NAME)
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")")
                    self.Identity.listenToRecordUpdates(self.WB_Baseline, 'WB_BASELINE_ID', 'BASELINE_ID', 'BASELINE_NAME')

                    return self.IJobParam_OnLoadComplete()
                })
                .then(() => {
                    return self.loadPartitionBO()
                })
                .then((aPartitionBO: PartitionBO) => {
                    self.partitionBO = aPartitionBO

                    if (_.isNumber(self.drWB_Baseline.cif.WB_MDSET_ID) && self.drWB_Baseline.cif.WB_MDSET_ID > 0) {
                        let mruService = new MDUsageService()
                        return mruService.saveMDSetUsage(self.drWB_Baseline.cif.WB_MDSET_ID)
                    }
                    else {
                        self._bIsLoaded = true
                        self._OnLoadComplete = undefined
                        self.LoadProgressProvider.finish()
                        resolve(self)
                    }
                })
                .then((ignore) => {
                    self._bIsLoaded = true
                    self._OnLoadComplete = undefined
                    self.LoadProgressProvider.finish()
                    resolve(self)
                })
                .catch(function (err: any) {
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.exception(err)
                error(err)
                return
            })

        })

        return this._OnLoadComplete
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded
    }


    async Validate(options?: any): Promise<IBusinessObject> {

        const self = this

        try {

            this.ValidateMessageProvider.clearMessages()
            this.ValidateProgressProvider.start("Validating " + self.WB_Baseline.cif.BASELINE_NAME + "(" + self.WB_Baseline.cif.BASELINE_ID + ")")

            self.ValidateOptions = options || {}

            if (self.dbContext == undefined) {
                const sError = "Unable to determine dbContext for to Validate Baseline."
                self.ValidateProgressProvider.exception(sError)
                throw new Error(sError)
            }

            if (!self.WB_Baseline.isActive) {
                self._bIsValidated = true;
                self.ValidateProgressProvider.finish();
                return self
            }
            await BaselineBOValidations.Validate(self)

            if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) self._bIsValidated = false
            else self._bIsValidated = true
            
            self.ValidateProgressProvider.finish()
            return self

        } catch (err) {
            self._bIsValidated = false
            self.ValidateProgressProvider.exception(err)
            return self
        }
    }

    get bIsValidated(): boolean {
        return this._bIsValidated
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    async SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {

        const self = this
        const wbBaseline = <IwbBaseline>self.drWB_Baseline
        this.SaveProgressProvider.start("Saving " + wbBaseline.BASELINE_NAME + "(" + wbBaseline.BASELINE_ID + ")")
        let auditService: AuditService = undefined

        // Doesn't need to do anything yet
        // All the records are in the store, since they were added through find or createNewRecord.
        // -- We could search the tree if the there is any record where store isn't set.
        try {
            if (self.dbContext == undefined) {
                self._bIsSaved = false
                const sError = "Unable to determine dbContext to Save Baseline."
                self.SaveProgressProvider.exception(sError)
                throw new Error(sError)
            }

            self.SaveOptions = options || {}

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext)
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext)
            }

            // Add code to do individual BO save for dbContextRows....
            await self.Validate({})

            if (!self.bIsValidated) {
                self._bIsSaved = false
                self.SaveProgressProvider.finish()
                return self
            }

            auditService = await BaselineBOAudit.BaselineAuditBO(self)

            await saveFunction(esWB_Baseline, self.drWB_Baseline, {})

            let tasks: Promise<any>[] = []

            tasks.push(saveFunction(esWB_Partition, self.partitionBO.WB_Partition))
            _.forEach(self.partitionBO.WB_Partition.WB_PartitionRRows, (aRow: WB_PartitionRRow) => {
                tasks.push(saveFunction(esWB_PartitionR, aRow))
            })

            await Promise.all(tasks)

            if (!self.JobParam) {
                if (self.WB_Baseline.isActive) {
                    await self.IJobParam_OnLoadComplete();
                }
            }
            if (self.JobParam) {
                self.JobParam.id_ui = self.WB_Baseline.cif.BASELINE_ID;
                self.JobParam.name = self.WB_Baseline.cif.BASELINE_NAME;
                await self.JobParam.Save(bBatch);
            }

            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true
                })
            } else {
                self._bIsSaved = true
            }

            self.SaveProgressProvider.finish()
            return self
            
        } catch (err) {
            self.SaveProgressProvider.exception(err)
            return self
        } finally {
            if (auditService)
                await auditService.Save(bBatch)
        }
    }

    get bIsSaved(): boolean {
        return this._bIsSaved
    }

    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private async DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this
        const wbBaseline = <IwbBaseline>self.drWB_Baseline
        this.DeleteMessageProvider.clearMessages()
        this.DeleteProgressProvider.start("Deleting " + wbBaseline.BASELINE_NAME + "(" + wbBaseline.WB_BASELINE_ID + ")")
        try {
            // 1. Validate if we can delete the Baseline

            // 2. If Valid, mark all the records for deletion.
            self.drWB_Baseline.markDeleted();

            await self.partitionBO.Delete();

            if (self.JobParam) {
                await self.JobParam.Delete(bSaveNow);
            }

            if (bSaveNow) {
                await self.SaveUsing(false);
            }

            self._bIsDeleted = true;
            self.DeleteProgressProvider.finish();
        } catch (err) {
            this.DeleteProgressProvider.exception(err);
        }
        return self;
    }


    get bIsDeleted(): boolean {
        return this._bIsDeleted
    }

    //</editor-fold>

    loadPartitionBO(): Promise<IBusinessObject> {
        return new Promise<IBusinessObject>((resolve, error) => {
            let self = this
            let partitionId: number = self.WB_Baseline.cif.WB_PARTITION_ID
            let partitionBO = new PartitionBO({dbContext: self.dbContext})

            partitionBO.Load(partitionId)
                .then((aPartitionBO: PartitionBO) => {
                    resolve(aPartitionBO)
                })
                .catch((err) => {
                    error(err)
                })
        })
    }

    //<editor-fold desc = "Change events">

    private Baseline_onRecordChange(record: WB_BaselineRow, propertyName: string, propertyValue: any, options: BaselineBO) {
        this.BaselinePropertyChanged.next({
            BaselineBO: options,
            Row: record,
            PropertyChanged: propertyName,
            PropertyValue: propertyValue
        })
        this.WB_Baseline.WB_PartitionRow.cif.WB_CUSTH_ID = this.WB_Baseline.cif.WB_CUSTH_ID
        this.WB_Baseline.WB_PartitionRow.cif.WB_PRDH_ID = this.WB_Baseline.cif.WB_PRDH_ID
        this.WB_Baseline.WB_PartitionRow.cif.WB_BUSINESSUNIT_ID = this.WB_Baseline.cif.WB_BUSINESSUNIT_ID

        this.partitionBO.PartitionHierarchyChanged.next(this.partitionBO)
    }

    get BaselinePropertyChanged(): BehaviorSubject<any> {
        return this._BaselinePropertyChanged
    }

    //</editor-fold>

    //<editor-fold desc = "Job Param">

    _jobParam: IJobParam;

    async IJobParam_OnLoadComplete(): Promise<void> {
        if (!_.isEmpty(this._jobParam)) {
            return;
        }
        const jpMgr = new JobParamManager();
        const wbBaseline: IwbBaseline = this.WB_Baseline.cif;

        this._jobParam = await jpMgr.findOrCreate({
            dbContext: this.dbContext,
            JobSchedulerModuleId: "RefBaseline",

            sourceType: "Baseline",
            sourceWbId: wbBaseline.WB_BASELINE_ID,

            appExecId: "Baseline",
            wbTargetId: wbBaseline.WB_BASELINE_ID,

            wbMasterDataSetId: wbBaseline.WB_MDSET_ID,
            wbCompanyId: this.WB_Baseline.WB_SalesOrgRow?.WB_CompanyRow?.cif.WB_COMPANY_ID,
            wbSalesOrgId: wbBaseline.WB_SALESORG_ID,

            JobParamId: wbBaseline.BASELINE_ID,
            JobParamName: wbBaseline.BASELINE_NAME,
            JobProcessorType: "RefBaselineProcessor",
            DependentTriggerId: "RefBaseline",
        });
    }

    get JobParam(): IJobParam {
        return this._jobParam;
    }

    //</editor-fold>

    //<editor-fold desc = "Processing Status">

    _jobProcessRowForStatus: WB_JobProcessRow = null;

    /**
     * Get the status of the latest baseline refresh job, which is triggered through the scheduler UI.
     */
    async getRefreshStatus(): Promise<string> {
        if (!this.JobParam) {
            await this.IJobParam_OnLoadComplete();
        }
        const wbJobParamId: number = this.JobParam.wb_id;
        const yesterday: number = DateUtils.convertToSAPBWTs(DateUtils.subtractDays(new Date(), 1));

        // remove in-memory records. Frequent request like this may not always get the latest values of the record
        if (!_.isEmpty(this._jobProcessRowForStatus)) {
            await this.dbContext.purgeCache(esWB_Job_Process, this._jobProcessRowForStatus);
        }
        // Find the latest job process for the baseline in the last 24 hours
        const jobProcessRows: WB_JobProcessRow[] = await this.dbContext.findAll(esWB_Job_Process, {
            where: {
                WB_JOB_PARAM_ID: wbJobParamId,
                PROCESS_START_DTTS: { gte: yesterday },
                DELETED_IND: { neq: '1' },
            },
            limit: 1,
            orderBy: [["CREATED_DTTS", "DESC"], ["WB_JOBPROCESS_ID", "DESC"]],
        });
        if (_.isEmpty(jobProcessRows)) {
            this._jobProcessRowForStatus = null;
            return "";
        }
        this._jobProcessRowForStatus = _.first(jobProcessRows);
        return this._jobProcessRowForStatus.cif.PROCESS_STATUS;
    }

    //</editor-fold>

    //<editor-fold desc = "IRecordResolver operations">

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {

        let opts = boLoadOptions || {}
        let dbContext: PAdbContext

        if ('dbContext' in opts) {
            dbContext = opts.dbContext
        } else {
            dbContext = new PAdbContext({})
        }
        const bsln_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {},
            dctExcludeHasManyNamedRelations: {
                [esWB_Baseline]: [esWB_Baseline_Metrics]
            }
        }

        const aRecResolver = new ESRecordResolver(dbContext, bsln_options)

        return aRecResolver

    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {

        return new Promise<void>((resolve, error) => {
            recordResolver.findAllWithRelationships(esWB_Baseline, ids).then((result) => {
                resolve()
            }).catch((err) => {
                error(err)
            })
        })
    }

    static concurrentLoadCountHint(): number {
        return 10
    }

    //</editor-fold>

}
PAEnvironment.registerBusinessObjectClass(BaselineBO, "BaselineBO", "Baseline")
